import React, { createContext, useContext, useEffect, useState } from 'react';

import { directMessagesUrl, directMessageUrl } from '@/config/urls';
import {
	useListMyUnreadChatsQuery,
	useListTeamUnreadVideoChatsQuery,
} from '@/graphql';
import { isNil } from 'ramda';
import { useLocation, useParams } from 'react-router';

export interface UnreadChatsContext {
	chatIds: string[];
	videoChatIds: string[];
	refetchChats: () => void;
	refetchVideoChats: () => void;
	setChatIds: React.Dispatch<React.SetStateAction<string[]>>;
	setVideoChatIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const defaultValue: UnreadChatsContext = {
	chatIds: [],
	videoChatIds: [],
	refetchChats: () => null,
	refetchVideoChats: () => null,
	setChatIds: () => null,
	setVideoChatIds: () => null,
};

const UnreadChatsContext = createContext<UnreadChatsContext>(defaultValue);

export function useUnreadChats(): UnreadChatsContext {
	const context = useContext(UnreadChatsContext);
	if (isNil(context)) {
		throw new Error(`useTeam must be used within a TeamProvider`);
	}

	return context;
}

export const UnreadChatsProvider: React.FC = ({ children }) => {
	const {
		data: chatsQuery,
		dataUpdatedAt: chatsQueryUpdatedAt,
		refetch: refetchChats,
	} = useListMyUnreadChatsQuery(
		{},
		{
			refetchOnWindowFocus: true,
		},
	);
	const {
		data: videoChatsQuery,
		dataUpdatedAt: videoChatsQueryUpdatedAt,
		refetch: refetchVideoChats,
	} = useListTeamUnreadVideoChatsQuery(
		{},
		{
			refetchOnWindowFocus: true,
		},
	);
	const [chatIds, setChatIds] = useState(defaultValue.chatIds);
	const [videoChatIds, setVideoChatIds] = useState(defaultValue.videoChatIds);

	useEffect(() => {
		if (chatsQuery?.listMyChats.length) {
			console.log(
				'[UnreadChatsProvider] Updating unread chats due to query refetch',
			);
			setChatIds(chatsQuery.listMyChats.map((chat) => chat.id));
		}
	}, [chatsQuery, chatsQueryUpdatedAt]);

	useEffect(() => {
		if (videoChatsQuery?.listTeamVideoChats) {
			console.log(
				'[UnreadChatsProvider] Updating unread video chats due to query refetch',
			);
			setVideoChatIds(
				videoChatsQuery.listTeamVideoChats.map((chat) => chat.id),
			);
		}
	}, [videoChatsQuery, videoChatsQueryUpdatedAt]);

	// When the location changes, clear all messages related to this locaiton
	// useEffect(() => {
	// 	setChatIds((chatIds) =>
	// 		chatIds.filter(
	// 			(id) => location.pathname !== `${directMessagesUrl}/${id}`,
	// 		),
	// 	);
	// }, [location]);

	return (
		<UnreadChatsContext.Provider
			value={{
				chatIds,
				videoChatIds,
				refetchChats,
				refetchVideoChats,
				setChatIds,
				setVideoChatIds,
			}}
		>
			{children}
		</UnreadChatsContext.Provider>
	);
};

export default UnreadChatsProvider;
